/**
 * Het bedrag van één documentregel, met het juiste teken.
 *
 * Drie dingen die stuk voor stuk al eens fout gegaan zijn wanneer een scherm ze
 * zelf uitrekende:
 *
 * 1. **Terugval op `totalExclVat`.** `totalInclVat` is nullable; geïmporteerde
 *    facturen vullen soms alleen het excl-btw-veld. `Number(null)` is 0 en
 *    `Number(undefined)` is `NaN` — dat laatste besmet een hele optelsom.
 *    Vandaar dezelfde terugval als de backend: `COALESCE(totalInclVat,
 *    totalExclVat)`.
 * 2. **`Math.abs`.** De importer bewaart factuurregels als *negatieve* bedragen
 *    (omgekeerd teken t.o.v. de Odoo-boekingsregel), de manuele upload niet.
 *    Zonder `abs` hangt het teken dus af van hoe de regel binnengekomen is.
 * 3. **Creditnota's aftrekken.** Een creditnota *verlaagt* de kost. Het teken
 *    van de regel zegt dat niet — dat is padafhankelijk (zie 2) — dus is
 *    `document.isCreditNote` de bron van waarheid, net als in de
 *    backend-aggregaties (`CASE WHEN doc."isCreditNote" THEN -ABS(…)`).
 *
 * @returns het bedrag; negatief bij een creditnota, 0 wanneer er geen bruikbaar
 *          getal op de regel staat (nooit `NaN`).
 */
export const documentLineAmount = (line: {
  totalInclVat?: number | string | null;
  totalExclVat?: number | string | null;
  document?: { isCreditNote?: boolean } | null;
}): number => {
  const ruw = line?.totalInclVat ?? line?.totalExclVat ?? 0;
  const bedrag = Math.abs(Number(ruw));
  if (!Number.isFinite(bedrag)) return 0;
  return line?.document?.isCreditNote ? -bedrag : bedrag;
};

/** Som van `documentLineAmount` over een reeks regels. */
export const sumDocumentLineAmounts = (
  lines: Parameters<typeof documentLineAmount>[0][] | null | undefined,
): number => (lines ?? []).reduce((sum, line) => sum + documentLineAmount(line), 0);
